import { Trans, useLingui } from '@lingui/react/macro'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  MaterialIcon,
  toast,
} from '@oranje/ui'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { useSwitchActiveRoleMutation } from '@/app/sessionApi'
import { roleLabelOf } from '@/shared/constants/roles'
import { apiErrorMessage } from '@/shared/lib/apiError'
import type { SessionUser } from '@/shared/types/session.types'

/**
 * «Cambiar función» en el pie del menú (funciones adicionales, 2026-11-02).
 * Solo existe para quien tiene MÁS DE UNA función: con la de su alta no se
 * pinta nada —ni un control apagado ni un hueco—. Una sola función activa a
 * la vez: al cambiar se tira lo cacheado, se va al inicio de esa función y
 * se avisa con qué se trabaja ahora.
 */
export function RoleSwitcher({
  session,
  onSwitched,
}: {
  session: SessionUser
  /** Para cerrar el cajón del menú en el teléfono. */
  onSwitched?: () => void
}): ReactNode {
  const { t } = useLingui()
  const navigate = useNavigate()
  const [switchRole, { isLoading }] = useSwitchActiveRoleMutation()
  const roles = session.availableRoles ?? []

  if (roles.length <= 1) return null

  const activeTitle = roleLabelOf(session.roleId).title

  async function switchTo(roleCode: string): Promise<void> {
    if (roleCode === session.roleId) return
    try {
      await switchRole(roleCode).unwrap()
      onSwitched?.()
      // `/` lleva al inicio de la función nueva (RoleHome).
      void navigate('/', { replace: true })
      toast.success(t`Ahora trabajas como ${roleLabelOf(roleCode).title}`)
    } catch (error) {
      toast.error(
        apiErrorMessage(error, { fallback: t`No se pudo cambiar de función. Inténtalo de nuevo.` }),
      )
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={isLoading}
          aria-label={t`Cambiar función. Ahora trabajas como ${activeTitle}`}
          className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-line px-2.5 py-1.5 text-xs font-semibold text-ink-2 transition-colors hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-o-500 disabled:opacity-50"
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <MaterialIcon name="swap_horiz" className="text-base" />
            <span className="truncate">
              <Trans>Cambiar función</Trans>
            </span>
          </span>
          <MaterialIcon name="expand_more" className="text-base" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="min-w-56">
        <DropdownMenuLabel>
          <Trans>Trabajar como</Trans>
        </DropdownMenuLabel>
        {roles.map((role) => {
          const isActive = role.code === session.roleId
          return (
            <DropdownMenuItem
              key={role.code}
              disabled={isActive || isLoading}
              aria-current={isActive ? 'true' : undefined}
              onSelect={() => {
                void switchTo(role.code)
              }}
              className="flex items-center justify-between gap-3"
            >
              <span>{roleLabelOf(role.code).title}</span>
              {isActive && (
                <span className="flex items-center gap-1 text-xs text-ink-3">
                  <MaterialIcon name="check" className="text-base text-o-700" />
                  <Trans>Ahora</Trans>
                </span>
              )}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
